/**
 * DocumentStylePreview - CSS-based document rendering of clause wording.
 *
 * Renders clause text in a document-authentic style with serif font,
 * placeholder badges, list formatting, heading, table, and drafting-note
 * rendering via shared formatting utilities. `bare` drops the standalone
 * card chrome for embedding inside ClauseOptionCard; `showGuidance` drives
 * the Preview tab's drafting-notes toggle.
 *
 * Note: Uses Georgia/Times New Roman as a brand exception for document preview.
 */

import { clsx } from 'clsx';
import { formatClauseText } from './clauseTextFormatting';

interface DocumentStylePreviewProps {
  text: string;
  className?: string;
  /** Drop the standalone card chrome (border/shadow/padding) for embedding. */
  bare?: boolean;
  /** Render [GUIDANCE] drafting notes (default true). */
  showGuidance?: boolean;
}

export default function DocumentStylePreview({
  text,
  className,
  bare = false,
  showGuidance = true,
}: DocumentStylePreviewProps) {
  if (!text) {
    return (
      <div className="text-center py-8 text-slate-400 italic">
        No preview text available
      </div>
    );
  }

  return (
    <div
      className={clsx(
        // Standalone document-style container (skipped when embedded)
        !bare &&
          'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm p-6 max-w-[600px] mx-auto',
        // Serif font for legal document style (brand exception)
        'font-serif text-body-sm leading-relaxed text-slate-800 dark:text-slate-200',
        className
      )}
      style={{
        fontFamily: "Georgia, 'Times New Roman', serif",
      }}
    >
      {formatClauseText(text, { showGuidance })}
    </div>
  );
}
